import type { ThemedStyleSheetType, StyleSheetType } from './types';

/**
 * Returns a style object for the given theme. The style object, on trying to get
 * properties (e.g. styles.input in following example), automatically returns the
 * styling for the given property within the given theme, including any styles belonging
 * to the given modifiers (if these modifiers evaluate to true).
 *
 * @example
 * const stylesObject = {
 *     default: EStyleSheet.create({
 *         input: {
 *             color: '#000';
 *         }
 *     });
 *     default__isFocused: EStyleSheet.create({
 *         input: {
 *             color: '#999';
 *         }
 *     });
 * };
 * const modifiers = {
 *     isActive: true
 * }
 * const styles = getStyles(stylesObject, modifiers, 'default');
 * // styles.input returns '#000';
 *
 * const modifiers = {
 *     isFocused: true
 * }
 * const styles = getStyles(stylesObject, modifiers, 'default');
 * // styles.input returns '#999';
 *
 * @param {Object} stylesObject The styles object containing themes and `EStylesheet.create` result objects
 * @param {Object} modifiers Modifiers that are possibly existing in the stylesObject themes
 * @param {string} theme Style theme if applicable, defaults to 'default' of not found in `stylesObject`.
 */
export const getModifiedStyles = (
	stylesObject: ThemedStyleSheetType,
	modifiers = {},
	theme = 'default',
	flatten = false,
): StyleSheetType => {
	if (!stylesObject[theme]) {
		theme = 'default';
	}

	// returning a Proxy creates a situation where every property request on the returned object is ran
	//  through a getter
	const style = new Proxy(
		{},
		{
			get(target, property: string) {
				const result = [];

				result.push(stylesObject[theme][property]);

				// Map through all given modifiers, check whether they evaluate to true and whether there
				//  is styling specifically for this theme and modifier combination
				Object.keys(modifiers).map((modifier) => {
					modifiers[modifier] &&
						stylesObject[`${theme}__${modifier}`] &&
						stylesObject[`${theme}__${modifier}`][property] &&
						result.push(stylesObject[`${theme}__${modifier}`][property]);
				});

				// We are now left with an array of style object, where (I am assuming) the latter ones take priority in a react native environment
				// However, this does not work in a browser environment. Hence, we flatten this array into a single object if flatten is set
				if (flatten) return result.reduce((acc, cur) => ({ ...acc, ...cur }));

				return result;
			},
		},
	);

	return style;
};

export const reduceStyles = (arr: object[] | object) => {
	return Array.isArray(arr)
		? arr.reduce((acc: object, el: object) => ({ ...acc, ...(Array.isArray(el) ? reduceStyles(el) : el) }), {})
		: arr;
};
